/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { settingsStyles as styles } from '../../settings/screens/settingsScreen.styles';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { FilterSheetButton } from '../../shared/filters/FilterSheetButton';
import {
  CASH_DIFFERENCE_FILTER_OPTIONS,
  CASH_HISTORY_PRESET_CHIPS,
  CASH_HISTORY_SORT_OPTIONS,
  CASH_OPERATOR_FILTER_ALL,
  type CashDifferenceFilter,
  type CashHistoryRangePreset,
  type CashHistorySort,
  type CashOperatorFilter,
} from './cashList.constants';
import { cashOperatorFilterLabel } from './cashOperatorLabels';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal'
import { OperationalFilterChipGroup, OperationalFilterFieldLabel, OperationalFilterPickerRow } from '../../shared/filters/OperationalFilterPrimitives'
import { OperationalFiltersCard } from '../../shared/filters/OperationalFiltersCard'
import { useOperationalFilterLayout } from '../../shared/filters/useOperationalFilterLayout';

type Props = {
  historyRangePreset: CashHistoryRangePreset;
  onHistoryRangePresetChange: (preset: CashHistoryRangePreset) => void;
  historyRangeLabel: string;
  onOpenCustomModal: () => void;
  operatorFilter: CashOperatorFilter;
  onOperatorFilterChange: (value: CashOperatorFilter) => void;
  operatorOptions: Array<{ key: string; label: string }>;
  differenceFilter: CashDifferenceFilter;
  onDifferenceFilterChange: (value: CashDifferenceFilter) => void;
  historySort: CashHistorySort;
  onHistorySortChange: (value: CashHistorySort) => void;
  workspaceMode?: boolean;
  /** Resumen + botón que abre modal con filtros completos. */
  compact?: boolean;
  onOpenFiltersModal?: () => void;
  /** Oculta título interno (p. ej. dentro del modal de filtros). */
  hideTitle?: boolean;
  /** Flat ticket body (FilterSheetModal) — no amber card. */
  sheetMode?: boolean;
};

export function CashHistorialFiltersPanel({
  historyRangePreset,
  onHistoryRangePresetChange,
  historyRangeLabel,
  onOpenCustomModal,
  operatorFilter,
  onOperatorFilterChange,
  operatorOptions,
  differenceFilter,
  onDifferenceFilterChange,
  historySort,
  onHistorySortChange,
  workspaceMode,
  compact,
  onOpenFiltersModal,
  hideTitle,
  sheetMode = false,
}: Props) {
  const { variant } = useOperationalFilterLayout();
  const useWideChips = workspaceMode || variant === 'wide' || variant === 'workspace';
  const [operatorSheetOpen, setOperatorSheetOpen] = useState(false);
  const [differenceSheetOpen, setDifferenceSheetOpen] = useState(false);
  const [sortSheetOpen, setSortSheetOpen] = useState(false);

  const operatorLabel = useMemo(
    () => cashOperatorFilterLabel(operatorFilter, operatorOptions),
    [operatorFilter, operatorOptions]
  );

  const sortLabel = useMemo(
    () => CASH_HISTORY_SORT_OPTIONS.find((o) => o.value === historySort)?.label ?? 'Más reciente',
    [historySort]
  );

  const differenceLabel = useMemo(
    () => CASH_DIFFERENCE_FILTER_OPTIONS.find((o) => o.value === differenceFilter)?.label ?? 'Todos',
    [differenceFilter]
  );

  const operatorSheetOptions = useMemo(
    () => operatorOptions.map((opt) => ({ id: opt.key, name: opt.label })),
    [operatorOptions]
  );

  const sortSheetOptions = useMemo(
    () => CASH_HISTORY_SORT_OPTIONS.map((o) => ({ id: o.value, name: o.label })),
    []
  );

  const differenceSheetOptions = useMemo(
    () => CASH_DIFFERENCE_FILTER_OPTIONS.map((o) => ({ id: o.value, name: o.label })),
    []
  );

  const onPresetChange = useCallback(
    (id: CashHistoryRangePreset) => {
      onHistoryRangePresetChange(id);
      if (id === 'custom') onOpenCustomModal();
    },
    [onHistoryRangePresetChange, onOpenCustomModal]
  );

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (historyRangePreset !== '30d') count++;
    if (differenceFilter !== 'all') count++;
    if (operatorFilter !== CASH_OPERATOR_FILTER_ALL) count++;
    if (historySort !== 'closed_desc') count++;
    return count;
  }, [historyRangePreset, differenceFilter, operatorFilter, historySort]);

  if (compact && onOpenFiltersModal) {
    return (
      <FilterSheetButton
        activeCount={activeFilterCount}
        onOpen={onOpenFiltersModal}
        accessibilityLabel="Filtrar historial"
        variant={useWideChips || sheetMode ? 'desk' : 'ticket'}
      />
    );
  }

  const masterSheets = (
    <>
      <MasterFilterSheetModal
        visible={operatorSheetOpen}
        title="Filtrar por operador"
        options={operatorSheetOptions}
        selectedId={operatorFilter === CASH_OPERATOR_FILTER_ALL ? null : operatorFilter}
        allOptionLabel="Todos"
        appearance="ticket"
        onSelect={(id) => onOperatorFilterChange(id ?? CASH_OPERATOR_FILTER_ALL)}
        onClose={() => setOperatorSheetOpen(false)}
      />
      <MasterFilterSheetModal
        visible={differenceSheetOpen}
        title="Filtrar por arqueo"
        options={differenceSheetOptions}
        selectedId={differenceFilter}
        showAllOption={false}
        appearance="ticket"
        onSelect={(id) => {
          if (id) onDifferenceFilterChange(id as CashDifferenceFilter);
        }}
        onClose={() => setDifferenceSheetOpen(false)}
      />
      <MasterFilterSheetModal
        visible={sortSheetOpen}
        title="Ordenar cierres"
        options={sortSheetOptions}
        selectedId={historySort}
        showAllOption={false}
        appearance="ticket"
        onSelect={(id) => {
          if (id) onHistorySortChange(id as CashHistorySort);
        }}
        onClose={() => setSortSheetOpen(false)}
      />
    </>
  );

  if (workspaceMode) {
    return (
      <View style={toolbarStyles.wrap}>
        <View style={toolbarStyles.row}>
          <View style={toolbarStyles.chipsCol}>
            <OperationalFilterChipGroup
              ticketTone
              layout="hug"
              options={CASH_HISTORY_PRESET_CHIPS.map((c) => ({ value: c.id, label: c.label }))}
              value={historyRangePreset}
              onChange={onPresetChange}
              testIDPrefix="caja.history.range"
            />
          </View>
          {operatorOptions.length > 0 ? (
            <OperationalFilterPickerRow
              layout="inline"
              label="Operador"
              valueLabel={operatorLabel}
              active={operatorFilter !== CASH_OPERATOR_FILTER_ALL}
              onPress={() => setOperatorSheetOpen(true)}
              testID="caja.history.operator.picker"
            />
          ) : null}
          <OperationalFilterPickerRow
            layout="inline"
            label="Diferencia"
            valueLabel={differenceLabel}
            active={differenceFilter !== 'all'}
            onPress={() => setDifferenceSheetOpen(true)}
            testID="caja.history.difference.picker"
          />
          <OperationalFilterPickerRow
            layout="inline"
            label="Orden"
            valueLabel={sortLabel}
            active={historySort !== 'closed_desc'}
            onPress={() => setSortSheetOpen(true)}
            testID="caja.history.sort.picker"
          />
        </View>
        <View style={toolbarStyles.metaRow}>
          <Text style={sheetStyles.caption}>
            {historyRangeLabel} · el turno abierto se ve primero cuando cae en el rango
          </Text>
          {historyRangePreset === 'custom' ? (
            <TouchableOpacity onPress={onOpenCustomModal} accessibilityRole="button">
              <Text style={sheetStyles.link}>Editar fechas</Text>
            </TouchableOpacity>
          ) : null}
        </View>
        {masterSheets}
      </View>
    );
  }

  if (sheetMode || hideTitle) {
    return (
      <View style={sheetStyles.body}>
        <View style={sheetStyles.section}>
          <OperationalFilterFieldLabel>Período</OperationalFilterFieldLabel>
          <OperationalFilterChipGroup
            ticketTone
            options={CASH_HISTORY_PRESET_CHIPS.map((c) => ({ value: c.id, label: c.label }))}
            value={historyRangePreset}
            onChange={onPresetChange}
          />
          {historyRangePreset === 'custom' ? (
            <TouchableOpacity onPress={onOpenCustomModal} accessibilityRole="button">
              <Text style={sheetStyles.link}>Editar fechas</Text>
            </TouchableOpacity>
          ) : null}
          <Text style={sheetStyles.caption}>{historyRangeLabel}</Text>
        </View>

        <View style={sheetStyles.section}>
          <OperationalFilterFieldLabel>Diferencia</OperationalFilterFieldLabel>
          <OperationalFilterChipGroup
            ticketTone
            options={CASH_DIFFERENCE_FILTER_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            value={differenceFilter}
            onChange={onDifferenceFilterChange}
            testIDPrefix="caja.history.difference"
          />
        </View>

        <View style={sheetStyles.section}>
          <OperationalFilterFieldLabel>Detalle</OperationalFilterFieldLabel>
          {operatorOptions.length === 0 ? (
            <Text style={sheetStyles.caption}>No hay operadores en estos cierres.</Text>
          ) : (
            <OperationalFilterPickerRow
              layout="stack"
              label="Operador"
              valueLabel={operatorLabel}
              active={operatorFilter !== CASH_OPERATOR_FILTER_ALL}
              onPress={() => setOperatorSheetOpen(true)}
              testID="caja.history.operator.picker"
            />
          )}
          <OperationalFilterPickerRow
            layout="stack"
            label="Orden"
            valueLabel={sortLabel}
            active={historySort !== 'closed_desc'}
            onPress={() => setSortSheetOpen(true)}
            testID="caja.history.sort.picker"
          />
          {differenceFilter !== 'all' ? (
            <Text style={sheetStyles.caption}>Arqueo: {differenceLabel}</Text>
          ) : null}
        </View>
        {masterSheets}
      </View>
    );
  }

  return (
    <OperationalFiltersCard bodyStyle={{ gap: spacing.sm }}>
      {hideTitle ? null : <Text style={styles.cardTitle}>Filtros del historial</Text>}

      <OperationalFilterFieldLabel>Período</OperationalFilterFieldLabel>
      <OperationalFilterChipGroup
        ticketTone
        options={CASH_HISTORY_PRESET_CHIPS.map((c) => ({ value: c.id, label: c.label }))}
        value={historyRangePreset}
        onChange={onPresetChange}
      />
      {historyRangePreset === 'custom' ? (
        <TouchableOpacity onPress={onOpenCustomModal} accessibilityRole="button">
          <Text style={styles.caption}>Editar fechas</Text>
        </TouchableOpacity>
      ) : null}
      <Text style={styles.caption}>{historyRangeLabel}</Text>

      <OperationalFilterFieldLabel>Diferencia</OperationalFilterFieldLabel>
      <OperationalFilterChipGroup
        ticketTone
        options={CASH_DIFFERENCE_FILTER_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        value={differenceFilter}
        onChange={onDifferenceFilterChange}
        testIDPrefix="caja.history.difference"
      />

      <OperationalFilterFieldLabel>Operador</OperationalFilterFieldLabel>
      {operatorOptions.length === 0 ? (
        <Text style={styles.caption}>No hay operadores en estos cierres.</Text>
      ) : (
        <OperationalFilterPickerRow
          layout="stack"
          label="Operador"
          valueLabel={operatorLabel}
          active={operatorFilter !== CASH_OPERATOR_FILTER_ALL}
          onPress={() => setOperatorSheetOpen(true)}
          testID="caja.history.operator.picker"
        />
      )}

      <OperationalFilterFieldLabel>Orden</OperationalFilterFieldLabel>
      <OperationalFilterPickerRow
        layout="stack"
        label="Orden"
        valueLabel={sortLabel}
        onPress={() => setSortSheetOpen(true)}
        testID="caja.history.sort.picker"
      />

      {masterSheets}
    </OperationalFiltersCard>
  );
}

const toolbarStyles = StyleSheet.create({
  wrap: {
    gap: 6,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  chipsCol: {
    flexGrow: 1,
    minWidth: 220,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
});

const sheetStyles = StyleSheet.create({
  body: {
    gap: 18,
  },
  section: {
    gap: 8,
  },
  caption: {
    fontSize: 12,
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink3,
  },
  link: {
    fontSize: 13,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
    color: ticketColors.gold,
  },
});
